/**
 * utils/authRedirect.ts
 * Centralized redirect utility for post-logout navigation
 *
 * This utility handles all redirect logic after logout, providing:
 * - Centralized path exclusion rules (no redirect loops on auth pages)
 * - Role-based redirect destinations (admin vs user)
 * - Extensible logout modes for future features (local, global, revoked)
 * - Separation of concerns (pure logic vs side effects)
 *
 * IMPORTANT:
 * - This utility does NOT import React, React Router, or any Provider state
 * - It only uses window.location for reading/writing navigation state
 * - This ensures it works in passive tabs and background event listeners
 * - UI messaging (toasts, modals) is NOT handled here - stays in UI layer
 */

/**
 * Logout mode - determines redirect behavior
 * - 'local': User logged out from this device only (default)
 * - 'global': User logged out from all devices (Task 2 - "Logout Everywhere")
 * - 'revoked': Session revoked by admin (security event, always goes to /login)
 */
export type LogoutMode = 'local' | 'global' | 'revoked';

/**
 * Options for post-logout redirect
 */
export interface RedirectAfterLogoutOptions {
  /**
   * Logout mode - determines redirect behavior
   * @default 'local'
   */
  mode?: LogoutMode;

  /**
   * Current path to check for redirect eligibility
   * @default window.location.pathname
   */
  currentPath?: string;

  /**
   * User role - determines redirect destination
   * @default 'user'
   */
  userRole?: 'user' | 'admin';
}

/**
 * Paths that should NOT trigger a redirect after logout
 * These are public auth pages where users are already in the auth flow
 */
const REDIRECT_EXCLUSION_PATHS = [
  '/login',           // Regular user login page
  '/admin/login',     // Admin login page
  '/beta-application', // Beta application form (public)
  '/activate/'        // Account activation flow (public)
] as const;

/**
 * Determines if current path should trigger a redirect after logout
 *
 * Returns false if user is already on an auth/public page (avoid redirect loops)
 * Returns true if user is on a protected route (needs redirect)
 *
 * @param currentPath - Path to check (e.g., window.location.pathname)
 * @returns true if redirect is needed, false if already on auth page
 */
export function shouldRedirectAfterLogout(currentPath: string): boolean {
  // Check if current path matches any exclusion prefix
  for (const exclusionPath of REDIRECT_EXCLUSION_PATHS) {
    if (currentPath.startsWith(exclusionPath)) {
      return false; // Already on auth/public page, no redirect needed
    }
  }

  return true; // On protected route, redirect needed
}

/**
 * Gets the appropriate redirect destination after logout
 *
 * Logic:
 * - For 'revoked' mode: Always '/login' (security event, treat as untrusted)
 * - For 'local' or 'global' modes:
 *   - Admin users → '/admin/login'
 *   - Regular users → '/login'
 *
 * @param options - Redirect options (mode, userRole)
 * @returns Path string (e.g., '/login', '/admin/login')
 */
export function getLogoutRedirectPath(options: RedirectAfterLogoutOptions): string {
  const mode = options.mode ?? 'local';
  const userRole = options.userRole ?? 'user';

  // For revoked sessions, always go to regular login (security event)
  if (mode === 'revoked') {
    return '/login';
  }

  // For local/global logout, route based on user role
  // Admin users go to admin login, regular users go to regular login
  return userRole === 'admin' ? '/admin/login' : '/login';
}

/**
 * Performs post-logout redirect if needed
 *
 * Main entry point for all logout redirect logic. Handles:
 * 1. Checking if current path needs redirect (avoid loops)
 * 2. Determining correct redirect destination (role-based)
 * 3. Performing the redirect (window.location.href)
 *
 * This function is safe to call from:
 * - React event handlers (click handlers)
 * - Async event listeners (Supabase auth state change)
 * - Storage event listeners (cross-tab logout sync)
 * - Background processes (offline logout cleanup)
 *
 * @param options - Redirect options (mode, currentPath, userRole)
 */
export function redirectAfterLogout(options?: RedirectAfterLogoutOptions): void {
  // Get current path (from options or window.location.pathname)
  const currentPath = options?.currentPath ?? window.location.pathname;

  // Check if redirect is needed
  if (!shouldRedirectAfterLogout(currentPath)) {
    console.log('🔄 authRedirect: Already on auth page, skipping redirect from:', currentPath);
    return;
  }

  // Get redirect destination
  const redirectPath = getLogoutRedirectPath(options ?? {});

  // Log redirect action
  console.log('🔄 authRedirect: Redirecting after logout:', {
    from: currentPath,
    to: redirectPath,
    mode: options?.mode ?? 'local',
    userRole: options?.userRole ?? 'user'
  });

  // Perform redirect (side effect)
  window.location.href = redirectPath;
}
